<script lang="ts" setup>
  import { useAuthStore } from '@tundr/auth';
  import { Badge, DropdownItem } from '@tundr/ds-components';
  import { useLocale } from '@tundr/i18n';
  import { useModal } from '@tundr/modal';
  import { usePermissionsStore } from '@tundr/permissions';
  import { SidebarUserMenu, type SidebarVariantType } from '@tundr/sidebar';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import { computed, onMounted } from 'vue';
  import { ENV_VARS } from '@core/config/env';
  import { getRoleAccent } from '@core/utils/role-accent.ts';
  import PrivacyModal from '@modules/app/serviceAgreements/components/PrivacyModal/PrivacyModal.vue';
  import { useServiceAgreements } from '@modules/app/serviceAgreements/composables/use-service-agreements';
  import { useLogout } from '@shared/composables/use-logout';

  type UserMenuProps = {
    collapsed: boolean;
    variant?: SidebarVariantType;
  };

  defineProps<UserMenuProps>();

  const { t } = useLocale('common');
  const authStore = useAuthStore();
  const permissionsStore = usePermissionsStore();

  const role = computed(() => permissionsStore.role);
  const roleBadge = computed(() => getRoleAccent(role.value?.id ?? ''));

  const { openModal } = useModal();

  const { privacyAcceptance, termsAcceptance } = useServiceAgreements();

  const { onLogout } = useLogout();

  onMounted(() => {
    if (!privacyAcceptance || !termsAcceptance) {
      openModal({
        component: PrivacyModal,
      });
    }
  });

  const userDisplayName = computed(() =>
    `${authStore.user?.firstName ?? ''} ${authStore.user?.lastName ?? ''}`.trim(),
  );

  const privacyDocumentUrl = ENV_VARS.serviceAgreements.privacy;
  const termsDocumentUrl = ENV_VARS.serviceAgreements.terms;

  const onDownloadPrivacyDocument = () => {
    window.open(privacyDocumentUrl, '_blank', 'noopener,noreferrer');
  };

  const onDownloadTermsDocument = () => {
    window.open(termsDocumentUrl, '_blank', 'noopener,noreferrer');
  };
</script>

<template>
  <SidebarUserMenu
    :collapsed="collapsed"
    :variant="variant"
    :user-name="userDisplayName"
    :trigger-aria-label="t('profile_menu.actions.open_user_menu.aria_label')"
  >
    <template #badge>
      <Badge
        v-if="role"
        :color="roleBadge.badgeColor"
        :icon-name="roleBadge.icon"
        :text="role.name"
        test-id="user-menu-role-badge"
      />
    </template>
    <DropdownItem
      v-if="privacyDocumentUrl"
      :icon="TUNDR_ICONS.FILE"
      @click="onDownloadPrivacyDocument"
      test-id="menu-item-add-company"
      :label="t('serviceAgreements:privacy.short_link_text')"
    />
    <DropdownItem
      v-if="termsDocumentUrl"
      :icon="TUNDR_ICONS.FILE"
      @click="onDownloadTermsDocument"
      test-id="menu-item-switch-company"
      :label="t('serviceAgreements:terms.short_link_text')"
    />
    <DropdownItem
      :icon="TUNDR_ICONS.SIGNOUT"
      @click="() => onLogout()"
      test-id="menu-item-logout"
      :label="t('profile_menu.actions.logout.label')"
    />
  </SidebarUserMenu>
</template>
